import { Product } from "@/api/product-api";
import { AudioToHapticSection } from "@/v2-components/pages/tactsuit/audio-to-haptic-section";
import React from "react";
import { cn } from "@/libs/utils";
import { TactsuitAirInteractionScrollContainer } from "@/v2-components/pages/tactsuit/tactsuit-air/container/tactsuit-air-interaction-scroll-container";
import OfficiallyMetaCertified from "@/components/tactsuit/components/OfficiallyMetaCertified";
import ImmersionBeyondComparison from "@/components/tactsuit/components/ImmersionBeyondComparison";
import { ComponentId } from "@/constants";
import { WirelessFeature } from "@/v2-components/pages/tactsuit/GridsCard";

const FEATURE_01_URL = "/images/tactsuit/tactsuit-air/wireless.png";

export default function TactSuitAirContent({ product }: { product: Product }) {
  return (
    <>
      {/* ⚠️ 섹션 순서는 **Figma Air 프레임(`28715:139470`) 본문 y 좌표**를 따른다.
          존재 여부만 맞추고 순서를 안 본 상태였어서 두 곳이 어긋나 있었다(#5266).

            #3  y=4924  Immersion Across Every Platform  ← 여기가 첫 섹션
            #4a y=5492  PartA  Weight + Perfect Blend    = 인터랙션 스크롤
            #4b y=6292  PartB  Powerful Haptics for Music = AudioToHapticSection
            #5  y=7188  Latency-Free Wireless Support     = WirelessFeature
            #6  y=7870  Officially Meta Certified         ← Companion Software 직전
            #7  y=8166  bHaptics Companion Software       (부모가 렌더)

          바로 위가 `ProductHeroAndBenefits`(#1 히어로 + #2 Purchase Benefits, 48/64)라
          섹션 간격은 `Gap`(64/96)이 담당한다 — Air·Air Camo 만 다른 6개 제품보다
          좁아지는 문제가 있었다. 두 컴포넌트 모두 `ProductFeatureSections` 한 곳에서만
          쓰인다.
          #5266 에서 앞에 있던 "Details" 블록(이미지 캐러셀 + 색상 스와치)도 지웠다 —
          색상은 구매 레일이, 색상별 이미지는 상단 갤러리(`?color=`)가 이미 담당한다. */}
      <Gap>
        {/* 지운 Details 블록 안에 있던 `Overview` 앵커를 **첫 생존 섹션으로 옮겼다.**
            `Gap` **안**에 두는 이유는 마진 뒤 콘텐츠 시작점에 붙어야 종전 착지와
            같기 때문이다.
            ⚠️ 지금 이 앵커를 읽는 화면은 없다 — `idToScroll: ComponentId.OVERVIEW` 를
            쓰던 `TactsuitSubMenu` 는 **import 하는 곳이 0인 고아 모듈**이었고(실측: PDP
            에 Overview 탭이 렌더되지 않음) 2026-10-01 에 지웠다. 그래도 앵커를 지우지
            않고 옮긴 이유는 ① DK3 분기도 같은 앵커를 갖고 있어(`ProductFeatureSections`
            의 OVERVIEW) 제품 간 대칭이 유지되고 ② 서브메뉴가 다시 생길 때 착지점이
            필요한데, 그때 이 한 줄이 없으면 조용히 아무 데도 가지 않기 때문이다. 즉
            **보험이지 현재 의존이 아니다.** */}
        <div id={ComponentId.OVERVIEW} />
        <ImmersionBeyondComparison />
      </Gap>
      {/* ⚠️ 여기 있던 `LightweightAirDesignFeature`("Lightweight and stylish", 2.4lb,
          starter-bundle.mp4)를 제거했다 — **Figma 에 대응 섹션이 없다.**
          혼동하기 쉬운데, 설계의 `PartA`(y=5492: "Weight" + 큰 숫자 + "The Perfect Blend
          of Style and Immersion" + "comes in two styles")는 이 블록이 아니라 **바로 아래
          인터랙션 스크롤**이다 — `tactsuit-interaction-scroll-info.tsx` 가 그 네 문구를
          그대로 갖고 있다. 즉 무게 소개는 이미 아래에 있고 이 블록은 중복 주장이었다. */}
      <div className="mt-16 w-full bg-coolgray-10 laptop:mt-24">
        <TactsuitAirInteractionScrollContainer />
      </div>
      <AudioToHapticSection className="laptop:mt-0" slug={product.slug} />
      <Gap className="px-4">
        <WirelessFeature imageSrc={FEATURE_01_URL} />
      </Gap>
      {/* ⚠️ **`bHaptics Companion Software` 직전**이어야 한다(Figma #6 → #7). 부모
          (`ProductFeatureSections` 의 `TactSuitAirFeatureSections`)가 이 컴포넌트 바로
          뒤에 Companion Software 를 렌더하므로, 이 자리가 그 "직전" 이다.
          예전엔 맨 앞(Purchase Benefits 직후)에 있어서 설계상 자리에서 빠져 보였다. */}
      <Gap className="px-4">
        <OfficiallyMetaCertified />
      </Gap>
    </>
  );
}

export const Gap = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn("mt-16 laptop:mt-24", className)}>{children}</div>
);
